import './TestExecution.css';
import '../../styles/osPills.css';
import { useState, useEffect, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PlayCircle, FlaskConical, Search, ListFilter, ExternalLink, Expand, Info, CirclePlus, Bug, Columns3, Archive, Ellipsis, SquareMousePointer, Pen, GitCompareArrows, Rocket, Gauge } from 'lucide-react';
import { FaApple, FaAndroid, FaReact } from 'react-icons/fa';
import { TbDeviceMobileCog } from 'react-icons/tb';
import { FaFlutter } from 'react-icons/fa6';
import { SiApachecordova } from 'react-icons/si';
import { IoLogoCapacitor } from 'react-icons/io5';
import api from '../../api';
import { setPromotedInRuns, promotedFirst, canPromote } from './promoteRun';
import RcPill from '../../components/TestExecution/RcPill';
import RunHistoryDots from '../../components/TestExecution/RunHistoryDots';
import useLastExecutions, { packageForCycle } from '../../hooks/useLastExecutions';
import { QUALITY_SERVICE_API_URL } from '../../config';
import { useCurrentUser } from '../../context/CurrentUserContext';
import CreateTestRunModal from '../../components/TestExecution/CreateTestRunModal';
import CompareRunsPanel from '../../components/TestExecution/CompareRunsPanel';
import WipPill from '../../components/common/WipPill';
import ReleasePerformancePanel from '../../components/TestExecution/ReleasePerformancePanel';

const JIRA_BASE = 'https://contentsquare.atlassian.net/browse';
const JIRA_KEY_RE = /^[A-Z][A-Z0-9]+-\d+$/;

// Generic "⋯" actions menu. `items` is a list of { label, icon, onClick }.
function ActionsMenu({ title, items }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPos, setMenuPos] = useState(null);
  const btnRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return;
    function onDocMouseDown(e) {
      if (btnRef.current?.contains(e.target) || menuRef.current?.contains(e.target)) return;
      setMenuOpen(false);
    }
    function onKey(e) { if (e.key === 'Escape') setMenuOpen(false); }
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  function openMenu(e) {
    e.stopPropagation();
    const r = btnRef.current.getBoundingClientRect();
    setMenuPos({ top: r.bottom + 4, left: r.left });
    setMenuOpen(true);
  }

  return (
    <>
      <button ref={btnRef} className="te-run-menu-btn" title={title} onClick={openMenu}>
        <Ellipsis size={16} />
      </button>
      {menuOpen && menuPos && (
        <div
          ref={menuRef}
          className="te-run-menu"
          style={{ top: menuPos.top, left: menuPos.left }}
          onClick={e => e.stopPropagation()}
        >
          {items.map(({ label, icon: Icon, onClick }) => (
            <button
              key={label}
              className="te-run-menu-item"
              onClick={(e) => { e.stopPropagation(); setMenuOpen(false); onClick(); }}
            >
              <Icon size={13} />
              {label}
            </button>
          ))}
        </div>
      )}
    </>
  );
}

function statusSelectClass(status) {
  if (!status) return 'te-select-unexecuted';
  switch (status.toLowerCase()) {
    case 'passed':      return 'te-select-passed';
    case 'failed':      return 'te-select-failed';
    case 'in-progress': return 'te-select-inprogress';
    case 'skipped':     return 'te-select-skipped';
    default:            return 'te-select-unexecuted';
  }
}

function formatDateTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

const STATUS_OPTIONS = ['passed', 'failed', 'unexecuted', 'skipped', 'in-progress'];

const TE_FILTERABLE_FIELDS = [
  { label: 'Mobile OS',         key: 'mobileOs' },
  { label: 'Automation Status', key: 'automationStatus' },
  { label: 'Unit',              key: 'unit' },
  { label: 'Execution Status',  key: 'status' },
];

const TE_COLUMNS = [
  { label: 'Test Key',         key: 'testCaseKey' },
  { label: 'Test Summary',     key: 'title' },
  { label: 'Mobile Framework', key: 'mobileFramework' },
  { label: 'Mobile OS',        key: 'mobileOs' },
  { label: 'Automation',       key: 'automationStatus' },
  { label: 'Unit',             key: 'unit' },
  { label: 'Status',           key: 'status' },
  { label: 'Last 10 Executions', key: 'lastExecutions' },
  { label: 'Executed By',      key: 'executedBy' },
  { label: 'Updated',          key: 'updated' },
  { label: 'Defects',          key: 'defects' },
];
const TE_DISABLED_BY_DEFAULT = new Set(['mobileFramework', 'executedBy']);

const OS_CONFIG = {
  ios:     { label: 'iOS',     Icon: FaApple,   className: 'tm-os-pill tm-os-ios' },
  android: { label: 'Android', Icon: FaAndroid, className: 'tm-os-pill tm-os-android' },
};

const FRAMEWORK_CONFIG = {
  'react native': { label: 'React Native', Icon: FaReact,            className: 'tm-fw-pill tm-fw-react-native' },
  'native':       { label: 'Native',       Icon: TbDeviceMobileCog,  className: 'tm-fw-pill tm-fw-native' },
  'flutter':      { label: 'Flutter',      Icon: FaFlutter,          className: 'tm-fw-pill tm-fw-flutter' },
  'cordova':      { label: 'Cordova',      Icon: SiApachecordova,    className: 'tm-fw-pill tm-fw-cordova' },
  'capacitor':    { label: 'Capacitor',    Icon: IoLogoCapacitor,    className: 'tm-fw-pill tm-fw-capacitor' },
};

const CYCLE_PLATFORMS = [
  { key: 'android',      label: 'Android',      Icon: FaAndroid },
  { key: 'flutter',      label: 'Flutter',      Icon: FaFlutter },
  { key: 'ios',          label: 'iOS',          Icon: FaApple },
  { key: 'react native', label: 'React Native', Icon: FaReact },
];

const MISSING_VALUE = '__missing__';
const Missing = () => <span className="te-missing"><Info size={12} />missing</span>;

function OsPills({ values }) {
  if (!values?.length) return <Missing />;
  return (
    <span className="tm-os-pills">
      {values.map(v => {
        const cfg = OS_CONFIG[v.toLowerCase()] ?? { label: v, Icon: null, className: 'tm-os-pill tm-os-default' };
        return (
          <span key={v} className={cfg.className}>
            {cfg.Icon && <cfg.Icon size={11} />}
            {cfg.label}
          </span>
        );
      })}
    </span>
  );
}

function FrameworkPills({ values }) {
  const arr = Array.isArray(values) ? values : values ? values.split(',').map(v => v.trim()) : [];
  if (!arr.length) return <Missing />;
  return (
    <span className="tm-fw-pills">
      {arr.map(v => {
        const cfg = FRAMEWORK_CONFIG[v.toLowerCase()] ?? { label: v, Icon: null, className: 'tm-fw-pill tm-fw-default' };
        return (
          <span key={v} className={cfg.className}>
            {cfg.Icon && <cfg.Icon size={11} />}
            {cfg.label}
          </span>
        );
      })}
    </span>
  );
}

function TestExecution({ projects, selectedProject, onSelectProject, onCaseClick }) {
  const currentUser = useCurrentUser();
  const [searchParams, setSearchParams] = useSearchParams();
  const [cycles, setCycles] = useState([]);
  const [expandedCycle, setExpandedCycle] = useState(null);
  const [runsByCycle, setRunsByCycle] = useState({});
  const [perfRunsByCycle, setPerfRunsByCycle] = useState({});
  const [selectedRun, setSelectedRun] = useState(null);
  const [selectedPerfRun, setSelectedPerfRun] = useState(null);
  const [runData, setRunData] = useState(null);
  const { history: lastExecutions, loading: lastExecutionsLoading, error: lastExecutionsError } = useLastExecutions(
    useMemo(() => runData?.items.map(i => i.testCaseKey) ?? [], [runData?.items]),
    packageForCycle(cycles.find(c => c.id === runData?.testCycleId)?.name)
  );
  const [runLoading, setRunLoading] = useState(false);
  const [runError, setRunError] = useState(null);
  const [search, setSearch] = useState(() => searchParams.get('ex_search') || '');
  const [activeFilters, setActiveFilters] = useState(() => {
    const initial = [];
    TE_FILTERABLE_FIELDS.forEach(({ key }) => {
      const values = searchParams.getAll(`ex_${key}`);
      values.forEach(val => {
        initial.push({ field: key, value: val });
      });
    });
    return initial;
  });

  useEffect(() => {
    const next = new URLSearchParams(window.location.search);
    next.delete('ex_search');
    TE_FILTERABLE_FIELDS.forEach(({ key }) => {
      next.delete(`ex_${key}`);
    });
    if (search) {
      next.set('ex_search', search);
    }
    activeFilters.forEach(({ field, value }) => {
      next.append(`ex_${field}`, value);
    });
    setSearchParams(next, { replace: true });
  }, [search, activeFilters, setSearchParams]);
  const [filterOpen, setFilterOpen] = useState(false);
  const [filterMenuOpen, setFilterMenuOpen] = useState(false);
  const [filterMenuStep, setFilterMenuStep] = useState('field');
  const [filterMenuField, setFilterMenuField] = useState(null);
  const [colPickerOpen, setColPickerOpen] = useState(false);
  const filterMenuRef = useRef(null);
  const [visibleCols, setVisibleCols] = useState(
    () => new Set(TE_COLUMNS.map(c => c.key).filter(k => !TE_DISABLED_BY_DEFAULT.has(k)))
  );
  const [showArchived, setShowArchived] = useState(false);
  const [platformFilter, setPlatformFilter] = useState(null);
  const [defectModal, setDefectModal] = useState(null);
  const [defectInput, setDefectInput] = useState('');
  const [showCreateRunModal, setShowCreateRunModal] = useState(false);
  const [editRun, setEditRun] = useState(null);
  const [renameModal, setRenameModal] = useState(null);
  const [renameInput, setRenameInput] = useState('');
  const [renameSaving, setRenameSaving] = useState(false);
  const [renameCycleModal, setRenameCycleModal] = useState(null);
  const [renameCycleInput, setRenameCycleInput] = useState('');
  const [renameCycleSaving, setRenameCycleSaving] = useState(false);

  function updateParams(updates) {
    const next = new URLSearchParams(window.location.search);
    Object.entries(updates).forEach(([k, v]) => {
      if (v == null) next.delete(k);
      else next.set(k, v);
    });
    setSearchParams(next, { replace: true });
  }

  // Release performance runs of a cycle; none when the service has no such endpoint yet.
  function loadPerfRuns(cycleId) {
    api.get(`${QUALITY_SERVICE_API_URL}/hub/sdk-performance/release-runs`, { params: { testCycleId: cycleId } })
      .then(res => setPerfRunsByCycle(prev => ({ ...prev, [cycleId]: Array.isArray(res.data) ? res.data : [] })))
      .catch(() => setPerfRunsByCycle(prev => ({ ...prev, [cycleId]: [] })));
  }

  const filteredItems = useMemo(() => {
    if (!runData) return [];
    let items = runData.items;
    if (search.trim()) {
      const raw = search.trim();
      const urlMatch = raw.match(/\/browse\/([A-Z]+-\d+)/i);
      const keyMatch = raw.match(/^([A-Z]+-)?(\d+)$/i);
      if (urlMatch) {
        const k = urlMatch[1].toLowerCase();
        items = items.filter(i => i.testCaseKey.toLowerCase() === k || (i.title || '').toLowerCase().includes(k));
      } else if (keyMatch && keyMatch[1]) {
        const k = raw.toLowerCase();
        items = items.filter(i => i.testCaseKey.toLowerCase() === k || (i.title || '').toLowerCase().includes(k));
      } else if (keyMatch && !keyMatch[1]) {
        const num = keyMatch[2];
        items = items.filter(i =>
          i.testCaseKey.toLowerCase().endsWith(`-${num}`) ||
          (i.title || '').toLowerCase().includes(num) ||
          (i.folderPath || '').toLowerCase().includes(num)
        );
      } else {
        const q = raw.toLowerCase();
        items = items.filter(i =>
          i.testCaseKey.toLowerCase().includes(q) ||
          (i.title || '').toLowerCase().includes(q) ||
          (i.folderPath || '').toLowerCase().includes(q)
        );
      }
    }
    if (activeFilters.length > 0) {
      const grouped = activeFilters.reduce((acc, { field, value }) => {
        (acc[field] = acc[field] || []).push(value.toLowerCase());
        return acc;
      }, {});
      items = items.filter(item =>
        Object.entries(grouped).every(([field, values]) => {
          const raw = item[field];
          const isMissing = Array.isArray(raw) ? raw.length === 0 : (raw == null || raw === '');
          if (values.includes(MISSING_VALUE)) {
            if (isMissing) return true;
            const nonMissingValues = values.filter(v => v !== MISSING_VALUE);
            if (nonMissingValues.length === 0) return false;
            if (Array.isArray(raw)) return raw.some(v => nonMissingValues.includes(v.toString().toLowerCase()));
            return nonMissingValues.includes(raw.toString().toLowerCase());
          }
          if (Array.isArray(raw)) return raw.some(v => values.includes(v.toString().toLowerCase()));
          return values.includes((raw ?? '').toString().toLowerCase());
        })
      );
    }
    return items;
  }, [runData, search, activeFilters]);

  const visibleCycles = useMemo(
    () => cycles.filter(c =>
      (showArchived || c.endDate == null) &&
      (!platformFilter || c.name.toLowerCase().includes(platformFilter))
    ),
    [cycles, showArchived, platformFilter]
  );

  const fieldOptions = useMemo(() => {
    if (!runData) return {};
    const result = {};
    TE_FILTERABLE_FIELDS.forEach(({ key }) => {
      const hasMissing = runData.items.some(item => {
        const v = item[key];
        return Array.isArray(v) ? v.length === 0 : (v == null || v === '');
      });
      const vals = [...new Set(
        runData.items.flatMap(item => {
          const v = item[key];
          return Array.isArray(v) ? v : (v != null && v !== '' ? [v] : []);
        })
      )].sort((a, b) => a.toString().localeCompare(b.toString()));
      if (hasMissing) vals.push(MISSING_VALUE);
      result[key] = vals;
    });
    return result;
  }, [runData]);

  useEffect(() => {
    if (!selectedProject) {
      setCycles([]);
      setExpandedCycle(null);
      setRunsByCycle({});
      setPerfRunsByCycle({});
      setSelectedRun(null);
      setSelectedPerfRun(null);
      setRunData(null);
      return;
    }
    const urlCycle = searchParams.get('cycle');
    const urlRun = searchParams.get('run');
    const urlPerfRun = searchParams.get('perfRun');
    api.get(`${QUALITY_SERVICE_API_URL}/hub/test-execution/cycles`, { params: { project: selectedProject } })
      .then(res => {
        setCycles(res.data);
        if (urlCycle) {
          setExpandedCycle(urlCycle);
          loadPerfRuns(urlCycle);
          if (urlPerfRun && !urlRun) setSelectedPerfRun(urlPerfRun);
          api.get(`${QUALITY_SERVICE_API_URL}/hub/test-execution/runs`, { params: { testCycle: urlCycle } })
            .then(runsRes => {
              const runs = runsRes.data.map(r => ({ id: r.testRunId, name: r.name, promotedAs: r.promotedAs ?? [] }));
              setRunsByCycle(prev => ({ ...prev, [urlCycle]: runs }));
              if (urlRun) {
                setSelectedRun(urlRun);
                setRunLoading(true);
                setRunError(null);
                api.get(`${QUALITY_SERVICE_API_URL}/hub/test-execution/run/${urlRun}`)
                  .then(runRes => setRunData(runRes.data))
                  .catch(() => setRunError('Failed to load test run results.'))
                  .finally(() => setRunLoading(false));
              }
            })
            .catch(() => {});
        }
      })
      .catch(() => setCycles([]));
  }, [selectedProject]); // eslint-disable-line react-hooks/exhaustive-deps

  function handleCycleClick(cycle) {
    if (expandedCycle === cycle.id) {
      setExpandedCycle(null);
      setSelectedRun(null);
      setSelectedPerfRun(null);
      setRunData(null);
      updateParams({ cycle: null, run: null, perfRun: null });
      return;
    }
    setExpandedCycle(cycle.id);
    setSelectedRun(null);
    setSelectedPerfRun(null);
    setRunData(null);
    updateParams({ cycle: cycle.id, run: null, perfRun: null });
    loadPerfRuns(cycle.id);

    api.get(`${QUALITY_SERVICE_API_URL}/hub/test-execution/runs`, { params: { testCycle: cycle.id } })
      .then(res => setRunsByCycle(prev => ({
        ...prev,
        [cycle.id]: res.data.map(r => ({ id: r.testRunId, name: r.name, promotedAs: r.promotedAs ?? [] })),
      })))
      .catch(() => setRunsByCycle(prev => ({ ...prev, [cycle.id]: [] })));
  }

  function handleStatusChange(testCaseKey, newStatus) {
    if (!runData) return;
    api.put(`${QUALITY_SERVICE_API_URL}/hub/test-execution/run`, {
      testRunId: runData.testRunId,
      items: [{ testCaseKey, updatedBy: currentUser?.email, status: newStatus }],
    }).then(() => {
      setRunData(prev => ({
        ...prev,
        items: prev.items.map(item =>
          item.testCaseKey === testCaseKey ? { ...item, status: newStatus } : item
        ),
      }));
      if (newStatus === 'failed') {
        setDefectInput('');
        setDefectModal({ testCaseKey });
      }
    }).catch(() => {});
  }

  function openDefectModal(e, testCaseKey) {
    e.stopPropagation();
    setDefectInput('');
    setDefectModal({ testCaseKey });
  }

  function closeDefectModal() {
    setDefectModal(null);
    setDefectInput('');
  }

  function extractIssueKey(value) {
    const trimmed = value.trim();
    const match = trimmed.match(/(?:https?:\/\/[^/]+\/browse\/)?([A-Z][A-Z0-9]+-\d+)/);
    return match ? match[1] : trimmed;
  }

  function handleDefectSave() {
    if (!defectModal || !defectInput.trim() || !runData) return;
    const { testCaseKey } = defectModal;
    const issueKey = extractIssueKey(defectInput);
    const item = runData.items.find(i => i.testCaseKey === testCaseKey);
    const existing = item?.defects || [];
    if (existing.includes(issueKey)) { closeDefectModal(); return; }
    const updated = [...existing, issueKey];
    api.put(`${QUALITY_SERVICE_API_URL}/hub/test-execution/run`, {
      testRunId: runData.testRunId,
      items: [{ testCaseKey, updatedBy: currentUser?.email, defects: updated }],
    }).then(() => {
      setRunData(prev => ({
        ...prev,
        items: prev.items.map(i =>
          i.testCaseKey === testCaseKey ? { ...i, defects: updated } : i
        ),
      }));
      closeDefectModal();
    }).catch(() => {});
  }

  useEffect(() => {
    if (!filterMenuOpen) return;
    function handleClickOutside(e) {
      if (filterMenuRef.current && !filterMenuRef.current.contains(e.target)) {
        setFilterMenuOpen(false);
        setFilterMenuStep('field');
        setFilterMenuField(null);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [filterMenuOpen]);

  function handleEditRun(run) {
    api.get(`${QUALITY_SERVICE_API_URL}/hub/test-execution/run/${run.id}`)
      .then(res => setEditRun(res.data))
      .catch(() => {});
  }

  function handleRenameRun(run) {
    setRenameInput(run.name ?? '');
    setRenameModal({ id: run.id, name: run.name ?? '' });
  }

  function closeRenameModal() {
    setRenameModal(null);
    setRenameInput('');
    setRenameSaving(false);
  }

  function handleRenameSave() {
    const name = renameInput.trim();
    if (!renameModal || !name || name === renameModal.name) { closeRenameModal(); return; }
    const runId = renameModal.id;
    setRenameSaving(true);
    api.put(`${QUALITY_SERVICE_API_URL}/hub/test-execution/run/edit`, {
      testRunId: runId,
      name,
      updatedBy: currentUser?.email,
    })
      .then(() => {
        setRunsByCycle(prev => {
          const next = {};
          for (const [cycleId, runs] of Object.entries(prev)) {
            next[cycleId] = runs.map(r => (r.id === runId ? { ...r, name } : r));
          }
          return next;
        });
        if (selectedRun === runId) {
          setRunData(prev => (prev ? { ...prev, name } : prev));
        }
        closeRenameModal();
      })
      .catch(() => setRenameSaving(false));
  }

  function handlePromoteRun(run) {
    const promoted = !run.promotedAs?.length;
    api.put(`${QUALITY_SERVICE_API_URL}/hub/test-execution/run/promote`, { testRunId: run.id, promoted })
      .then(res => {
        // The backend picks the kind from the run name; on removal we clear every kind the run held.
        const types = promoted ? [res.data.type] : run.promotedAs;
        setRunsByCycle(prev => {
          const next = {};
          for (const [cycleId, runs] of Object.entries(prev)) {
            next[cycleId] = runs.some(r => r.id === run.id)
              ? types.reduce((acc, type) => setPromotedInRuns(acc, run.id, type, promoted), runs)
              : runs;
          }
          return next;
        });
      })
      .catch(() => {});
  }

  // A cycle holds a single performance release candidate: promoting one clears the others.
  function handlePromotePerfRun(cycleId, perfRun) {
    const promoted = !perfRun.promotedAs?.length;
    api.put(`${QUALITY_SERVICE_API_URL}/hub/sdk-performance/release/promote`, { runId: perfRun.id, promoted })
      .then(() => {
        setPerfRunsByCycle(prev => ({
          ...prev,
          [cycleId]: (prev[cycleId] ?? []).map(r => ({
            ...r,
            promotedAs: promoted && r.id === perfRun.id ? ['performance'] : [],
          })),
        }));
      })
      .catch(() => {});
  }

  const perfRuns = cycleId => perfRunsByCycle[cycleId] ?? [];

  function renderPerfRow(cycleId, perfRun) {
    return (
      <div key={`perf-${perfRun.id}`} className={`te-run-row ${selectedPerfRun === String(perfRun.id) ? 'active' : ''}`}>
        <button
          className={`te-run-subitem te-perf-subitem ${selectedPerfRun === String(perfRun.id) ? 'active' : ''} ${perfRun.promotedAs?.length ? 'promoted' : ''}`}
          onClick={() => handlePerfRunClick(perfRun)}
        >
          {(perfRun.promotedAs ?? []).map(type => <RcPill key={type} type={type} />)}
          <Gauge size={18} />
          <span className="te-run-name">{perfRun.runName}</span>
          <WipPill />
        </button>
        <ActionsMenu
          title="Performance run actions"
          items={[{
            label: perfRun.promotedAs?.length ? 'Remove release candidate' : 'Promote as release candidate',
            icon: Rocket,
            onClick: () => handlePromotePerfRun(cycleId, perfRun),
          }]}
        />
      </div>
    );
  }

  function handleRenameCycle(cycle) {
    setRenameCycleInput(cycle.name ?? '');
    setRenameCycleModal({ id: cycle.id, name: cycle.name ?? '' });
  }

  function closeRenameCycleModal() {
    setRenameCycleModal(null);
    setRenameCycleInput('');
    setRenameCycleSaving(false);
  }

  function handleRenameCycleSave() {
    const name = renameCycleInput.trim();
    if (!renameCycleModal || !name || name === renameCycleModal.name) { closeRenameCycleModal(); return; }
    const cycleId = renameCycleModal.id;
    setRenameCycleSaving(true);
    api.put(`${QUALITY_SERVICE_API_URL}/hub/test-execution/cycle/edit`, {
      id: cycleId,
      name,
    })
      .then(() => {
        setCycles(prev => prev.map(c => (c.id === cycleId ? { ...c, name } : c)));
        closeRenameCycleModal();
      })
      .catch(() => setRenameCycleSaving(false));
  }

  // `compare` URL param: present (possibly empty) while comparing; value is the source run id.
  const compareSource = searchParams.get('compare');
  const comparing = selectedRun != null && compareSource !== null;

  function reloadSelectedRun() {
    if (!selectedRun) return;
    api.get(`${QUALITY_SERVICE_API_URL}/hub/test-execution/run/${selectedRun}`)
      .then(res => setRunData(res.data))
      .catch(() => {});
  }

  function handleCompareRun(run) {
    if (selectedRun !== run.id) handleRunClick(run);
    updateParams({ run: run.id, compare: '' });
  }

  function handleRunClick(run) {
    if (selectedRun === run.id && compareSource === null) return;
    if (selectedRun === run.id) return;
    setSelectedRun(run.id);
    setSelectedPerfRun(null);
    setSearch('');
    setActiveFilters([]);
    setRunData(null);
    setRunLoading(true);
    setRunError(null);
    updateParams({ run: run.id, compare: null, perfRun: null });
    api.get(`${QUALITY_SERVICE_API_URL}/hub/test-execution/run/${run.id}`)
      .then(res => setRunData(res.data))
      .catch(() => setRunError('Failed to load test run results.'))
      .finally(() => setRunLoading(false));
  }

  function handlePerfRunClick(perfRun) {
    setSelectedPerfRun(String(perfRun.id));
    setSelectedRun(null);
    setRunData(null);
    setRunError(null);
    updateParams({ perfRun: perfRun.id, run: null, compare: null });
  }

  return (
    <div className="tm-body">
      <div className="tm-folders-bar te-sidebar">
        <div className="te-platform-filter">
          {CYCLE_PLATFORMS.map(({ key, label, Icon }) => (
            <button
              key={key}
              className={`te-platform-btn ${platformFilter === key ? 'active' : ''}`}
              title={platformFilter === key ? `Clear ${label} filter` : `Show ${label} cycles`}
              aria-label={label}
              onClick={() => setPlatformFilter(f => (f === key ? null : key))}
            >
              <Icon size={18} />
            </button>
          ))}
        </div>
        {!selectedProject && (
          <p className="tm-folders-bar-empty">Select a project to view cycles.</p>
        )}
        {selectedProject && (
          <label
            className={`tm-subfolders-toggle ${showArchived ? 'active' : ''}`}
            title={showArchived ? 'Hide archived cycles' : 'Show archived cycles'}
            onClick={() => setShowArchived(o => !o)}
          >
            <Archive size={13} />
            <span>Include archived test cycles</span>
            <span className={`tm-subfolders-switch ${showArchived ? 'on' : ''}`}>
              <span className="tm-subfolders-thumb" />
            </span>
          </label>
        )}
        {selectedProject && cycles.length === 0 && (
          <p className="tm-folders-bar-empty">No TestCycles exist for this project</p>
        )}
        {selectedProject && visibleCycles.map(cycle => (
          <div key={cycle.id}>
            <div className={`te-cycle-row ${expandedCycle === cycle.id ? 'active' : ''}`}>
              <button
                className={`te-cycle-subitem ${expandedCycle === cycle.id ? 'active' : ''}`}
                onClick={() => handleCycleClick(cycle)}
              >
                {cycle.endDate != null ? <Archive size={18} className="te-cycle-archived-icon" title="Archived" /> : <PlayCircle size={18} />}
                {cycle.name}
              </button>
              <ActionsMenu
                title="Test cycle actions"
                items={[
                  { label: 'Rename test cycle', icon: Pen, onClick: () => handleRenameCycle(cycle) },
                ]}
              />
            </div>
            {expandedCycle === cycle.id && perfRuns(cycle.id).filter(r => r.promotedAs?.length).map(perfRun => renderPerfRow(cycle.id, perfRun))}
            {expandedCycle === cycle.id && promotedFirst(runsByCycle[cycle.id] ?? []).map(run => (
              <div key={run.id} className={`te-run-row ${selectedRun === run.id ? 'active' : ''}`}>
                <button
                  className={`te-run-subitem ${selectedRun === run.id ? 'active' : ''} ${run.promotedAs?.length ? 'promoted' : ''}`}
                  onClick={() => handleRunClick(run)}
                >
                  {(run.promotedAs ?? []).map(type => <RcPill key={type} type={type} />)}
                  <FlaskConical size={18} />
                  <span className="te-run-name">{run.name}</span>
                </button>
                <ActionsMenu
                  title="Test run actions"
                  items={[
                    { label: 'Edit test selection', icon: SquareMousePointer, onClick: () => handleEditRun(run) },
                    { label: 'Rename test run', icon: Pen, onClick: () => handleRenameRun(run) },
                    { label: 'Carry over results from another run', icon: GitCompareArrows, onClick: () => handleCompareRun(run) },
                    ...(run.promotedAs?.length || canPromote(run) ? [{
                      label: run.promotedAs?.length ? 'Remove release candidate' : 'Promote as release candidate',
                      icon: Rocket,
                      onClick: () => handlePromoteRun(run),
                    }] : []),
                  ]}
                />
              </div>
            ))}
            {expandedCycle === cycle.id && perfRuns(cycle.id).filter(r => !r.promotedAs?.length).map(perfRun => renderPerfRow(cycle.id, perfRun))}
          </div>
        ))}
      </div>

      <div className="tm-right">
        <div className="tm-content">
          {!selectedProject && (
            <p className="tm-state-msg">Select a project to view test cycles.</p>
          )}
          {selectedProject && !selectedRun && !selectedPerfRun && (
            <>
              <p className="tm-state-msg">Select a test run to view results</p>
              <p className="tm-state-msg">OR</p>
              <button className="te-new-run-btn" onClick={() => setShowCreateRunModal(true)} title="Create a new test run">
                + Create new test run
              </button>
            </>
          )}
          {selectedPerfRun && (
            <ReleasePerformancePanel
              key={selectedPerfRun}
              runId={selectedPerfRun}
              runName={(perfRunsByCycle[expandedCycle] ?? []).find(perfRun => String(perfRun.id) === selectedPerfRun)?.runName}
              cycleName={cycles.find(cycle => cycle.id === expandedCycle)?.name}
            />
          )}
          {selectedRun && runLoading && <p className="tm-state-msg">Loading…</p>}
          {selectedRun && runError && <p className="tm-state-msg tm-error">{runError}</p>}
          {comparing && runData && !runLoading && (
            <CompareRunsPanel
              targetRunId={selectedRun}
              targetName={runData.name}
              runs={runsByCycle[expandedCycle] ?? []}
              sourceId={compareSource}
              updatedBy={currentUser?.email}
              onSourceChange={id => updateParams({ compare: id })}
              onClose={() => updateParams({ compare: null })}
              onCopied={reloadSelectedRun}
            />
          )}
          {!comparing && selectedRun && runData && !runLoading && (
            <>
              <div className="te-run-header">
                <div className="te-run-title-row">
                  <h2 className="te-run-title">{runData.name}</h2>
                  {runData.criticalOnly && (
                    <span className="te-critical-only-badge">Critical tests only</span>
                  )}
                </div>
                <div className="te-stats-bar">
                  <div className="te-stat">
                    <span className="te-stat-value">{runData.items.length}</span>
                    <span className="te-stat-label">Total Test Runs</span>
                  </div>
                  <div className="te-stat">
                    <span className="te-stat-value te-stat-executed">
                      {runData.items.filter(i => i.status && i.status !== 'unexecuted' && i.status !== 'in-progress').length}
                    </span>
                    <span className="te-stat-label">Executed</span>
                  </div>
                  <div className="te-stat">
                    <span className="te-stat-value te-stat-remaining">
                      {runData.items.filter(i => !i.status || i.status === 'unexecuted' || i.status === 'in-progress').length}
                    </span>
                    <span className="te-stat-label">Unexecuted</span>
                  </div>
                </div>
                {(() => {
                  const total = runData.items.length;
                  const passed = runData.items.filter(i => i.status === 'passed').length;
                  const failed = runData.items.filter(i => i.status === 'failed').length;
                  const skipped = runData.items.filter(i => i.status === 'skipped').length;
                  const inProgress = runData.items.filter(i => i.status === 'in-progress').length;
                  const unexecuted = runData.items.filter(i => !i.status || i.status === 'unexecuted').length;
                  const passedPct = total ? (passed / total) * 100 : 0;
                  const failedPct = total ? (failed / total) * 100 : 0;
                  const skippedPct = total ? (skipped / total) * 100 : 0;
                  const inProgressPct = total ? (inProgress / total) * 100 : 0;
                  const unexecutedPct = total ? (unexecuted / total) * 100 : 0;
                  return (
                    <div className="te-progress-bar">
                      <div className="te-progress-passed" style={{ width: `${passedPct}%` }}>
                        {passed > 0 && <span>{passed}</span>}
                      </div>
                      <div className="te-progress-failed" style={{ width: `${failedPct}%` }}>
                        {failed > 0 && <span>{failed}</span>}
                      </div>
                      <div className="te-progress-skipped" style={{ width: `${skippedPct}%` }}>
                        {skipped > 0 && <span>{skipped}</span>}
                      </div>
                      <div className="te-progress-in-progress" style={{ width: `${inProgressPct}%` }}>
                        {inProgress > 0 && <span>{inProgress}</span>}
                      </div>
                      <div className="te-progress-unexecuted" style={{ width: `${unexecutedPct}%` }}>
                        {unexecuted > 0 && <span>{unexecuted}</span>}
                      </div>
                    </div>
                  );
                })()}
              </div>

              <div className="te-toolbar">
                <div className="te-toolbar-left">
                  <div className="te-search-wrap">
                    <Search size={15} className="te-search-icon" />
                    <input
                      type="text"
                      placeholder="Search by name, key (MOBILE-123), number (123), or Jira URL…"
                      value={search}
                      onChange={e => setSearch(e.target.value)}
                      className="te-search-input"
                    />
                  </div>
                </div>
                <div className="te-toolbar-right">
                  <div className="tm-col-picker-wrap">
                    <button
                      className={`te-filter-btn ${colPickerOpen ? 'active' : ''}`}
                      onClick={() => { setColPickerOpen(o => !o); setFilterOpen(false); }}
                      title="Columns"
                    >
                      <Columns3 size={16} />
                    </button>
                    {colPickerOpen && (
                      <div className="tm-col-picker-dropdown">
                        {TE_COLUMNS.map(col => (
                          <label key={col.key} className="tm-col-picker-item">
                            <input
                              type="checkbox"
                              checked={visibleCols.has(col.key)}
                              onChange={() => {
                                setVisibleCols(prev => {
                                  const next = new Set(prev);
                                  next.has(col.key) ? next.delete(col.key) : next.add(col.key);
                                  return next;
                                });
                              }}
                            />
                            {col.label}
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                  <button
                    className={`te-filter-btn ${filterOpen ? 'active' : ''}`}
                    onClick={() => { setFilterOpen(o => !o); setColPickerOpen(false); setFilterMenuOpen(false); setFilterMenuStep('field'); setFilterMenuField(null); }}
                  >
                    <ListFilter size={16} />
                    Filters
                    {activeFilters.length > 0 && <span className="tm-filter-badge">{activeFilters.length}</span>}
                  </button>
                </div>
              </div>

              {filterOpen && (
                <div className="tm-filter-panel">
                  {activeFilters.map((f, i) => {
                    const fieldLabel = TE_FILTERABLE_FIELDS.find(ff => ff.key === f.field)?.label ?? f.field;
                    return (
                      <span key={`${f.field}:${f.value}`} className="tm-filter-chip">
                        {fieldLabel}: {f.value === MISSING_VALUE ? <Missing /> : f.value}
                        <button
                          className="tm-filter-chip-remove"
                          onClick={() => setActiveFilters(prev => prev.filter((_, idx) => idx !== i))}
                          aria-label={`Remove ${fieldLabel}: ${f.value} filter`}
                        >×</button>
                      </span>
                    );
                  })}
                  <div className="tm-filter-add-wrap" ref={filterMenuRef}>
                    <button
                      className="tm-filter-add-btn"
                      onClick={() => { setFilterMenuOpen(o => !o); setFilterMenuStep('field'); setFilterMenuField(null); }}
                    >
                      + Add filter
                    </button>
                    {filterMenuOpen && (
                      <div className="tm-filter-dropdown">
                        {filterMenuStep === 'field' && (
                          <>
                            <div className="tm-filter-dropdown-header">Filter by</div>
                            {TE_FILTERABLE_FIELDS.map(ff => (
                              <button
                                key={ff.key}
                                className="tm-filter-dropdown-item"
                                onClick={() => { setFilterMenuField(ff.key); setFilterMenuStep('value'); }}
                              >
                                {ff.label}
                              </button>
                            ))}
                          </>
                        )}
                        {filterMenuStep === 'value' && filterMenuField && (
                          <>
                            <button
                              className="tm-filter-dropdown-back"
                              onClick={() => { setFilterMenuStep('field'); setFilterMenuField(null); }}
                            >
                              ← {TE_FILTERABLE_FIELDS.find(ff => ff.key === filterMenuField)?.label}
                            </button>
                            {(fieldOptions[filterMenuField] ?? []).map(val => (
                              <button
                                key={val}
                                className="tm-filter-dropdown-item"
                                onClick={() => {
                                  const alreadyAdded = activeFilters.some(
                                    f => f.field === filterMenuField && f.value === String(val)
                                  );
                                  if (!alreadyAdded) {
                                    setActiveFilters(prev => [...prev, { field: filterMenuField, value: String(val) }]);
                                  }
                                  setFilterMenuOpen(false);
                                  setFilterMenuStep('field');
                                  setFilterMenuField(null);
                                }}
                              >
                                {val === MISSING_VALUE ? <Missing /> : String(val)}
                              </button>
                            ))}
                            {(fieldOptions[filterMenuField] ?? []).length === 0 && (
                              <span className="tm-filter-dropdown-empty">No values</span>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                  {activeFilters.length > 0 && (
                    <>
                      <div className="tm-filter-spacer" />
                      <button
                        className="tm-filter-clear-btn"
                        onClick={() => setActiveFilters([])}
                      >
                        Clear all
                      </button>
                    </>
                  )}
                </div>
              )}
              <div className="tm-table-wrap">
                <table className="tm-table">
                  <thead>
                    <tr>
                      {TE_COLUMNS.filter(col => visibleCols.has(col.key)).map(col => (
                        <th key={col.key} style={col.key === 'defects' ? {textAlign: 'center'} : undefined}>{col.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredItems.map((item) => (
                      <tr key={item.testCaseKey} onClick={() => onCaseClick(item.testCaseId, item.testCaseKey)} className="tm-row-clickable">
                        {visibleCols.has('testCaseKey') && <td className="te-key" onClick={e => e.stopPropagation()}>
                          {JIRA_KEY_RE.test(item.testCaseKey) ? (
                            <a href={`${JIRA_BASE}/${encodeURIComponent(item.testCaseKey)}`} target="_blank" rel="noopener noreferrer">
                              <ExternalLink size={13} />
                              {item.testCaseKey}
                            </a>
                          ) : (
                            <span><ExternalLink size={13} />{item.testCaseKey}</span>
                          )}
                        </td>}
                        {visibleCols.has('title') && <td className="te-summary"><span className="tm-summary-cell"><Expand size={13} className="tm-expand-icon" />{item.title || '—'}</span></td>}
                        {visibleCols.has('mobileFramework') && <td><FrameworkPills values={item.mobileFramework} /></td>}
                        {visibleCols.has('mobileOs') && <td><OsPills values={item.mobileOs} /></td>}
                        {visibleCols.has('automationStatus') && <td>{item.automationStatus || '—'}</td>}
                        {visibleCols.has('unit') && <td>{item.unit ? item.unit : <Missing />}</td>}
                        {visibleCols.has('status') && <td onClick={e => e.stopPropagation()}>
                          <select
                            className={`te-status-select ${statusSelectClass(item.status)}`}
                            value={item.status || 'unexecuted'}
                            onChange={e => handleStatusChange(item.testCaseKey, e.target.value)}
                          >
                            {STATUS_OPTIONS.map(s => (
                              <option key={s} value={s}>{s}</option>
                            ))}
                          </select>
                        </td>}
                        {visibleCols.has('lastExecutions') && <td><RunHistoryDots executions={lastExecutions[item.testCaseKey]} loading={lastExecutionsLoading} error={lastExecutionsError} /></td>}
                        {visibleCols.has('executedBy') && <td>{item.executedBy || '—'}</td>}
                        {visibleCols.has('updated') && <td className="tm-date">{formatDateTime(item.updated)}</td>}
                        {visibleCols.has('defects') && <td onClick={e => e.stopPropagation()} className="te-defects-cell">
                          {item.defects?.length
                            ? item.defects.map(d => (
                                JIRA_KEY_RE.test(d)
                                  ? <a key={d} href={`${JIRA_BASE}/${encodeURIComponent(d)}`} target="_blank" rel="noopener noreferrer" className="te-defect-tag"><Bug size={11} />{d}</a>
                                  : <span key={d} className="te-defect-tag"><Bug size={11} />{d}</span>
                              ))
                            : null}
                          {!item.defects?.length && (
                            <button className="te-defect-add-btn" title="Add defect" onClick={e => openDefectModal(e, item.testCaseKey)}>
                              <CirclePlus size={15} />
                            </button>
                          )}
                        </td>}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="te-footer">
                <span>
                  {filteredItems.length === runData.items.length
                    ? `${runData.items.length} test cases`
                    : `${filteredItems.length} of ${runData.items.length} test cases (filtered)`}
                </span>
              </div>
            </>
          )}
        </div>
      </div>
      {defectModal && (
        <div className="te-modal-overlay" onClick={closeDefectModal}>
          <div className="te-modal" onClick={e => e.stopPropagation()}>
            <h3 className="te-modal-title">Add Jira defect</h3>
            <p className="te-modal-sub">Paste a Jira link or issue key for <strong>{defectModal.testCaseKey}</strong></p>
            <input
              className="te-modal-input"
              type="text"
              placeholder="e.g. https://contentsquare.atlassian.net/browse/MOBILE-123"
              value={defectInput}
              onChange={e => setDefectInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleDefectSave(); if (e.key === 'Escape') closeDefectModal(); }}
              autoFocus
            />
            <div className="te-modal-actions">
              <button className="te-modal-cancel" onClick={closeDefectModal}>Cancel</button>
              <button className="te-modal-save" onClick={handleDefectSave} disabled={!defectInput.trim()}>Save</button>
            </div>
          </div>
        </div>
      )}
      {renameModal && (
        <div className="te-modal-overlay" onClick={closeRenameModal}>
          <div className="te-modal" onClick={e => e.stopPropagation()}>
            <h3 className="te-modal-title">Rename test run</h3>
            <p className="te-modal-sub">Enter a new name for this test run</p>
            <input
              className="te-modal-input"
              type="text"
              value={renameInput}
              onChange={e => setRenameInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleRenameSave(); if (e.key === 'Escape') closeRenameModal(); }}
              autoFocus
            />
            <div className="te-modal-actions">
              <button className="te-modal-cancel" onClick={closeRenameModal}>Cancel</button>
              <button
                className="te-modal-save"
                onClick={handleRenameSave}
                disabled={!renameInput.trim() || renameInput.trim() === renameModal.name || renameSaving}
              >
                {renameSaving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
      {renameCycleModal && (
        <div className="te-modal-overlay" onClick={closeRenameCycleModal}>
          <div className="te-modal" onClick={e => e.stopPropagation()}>
            <h3 className="te-modal-title">Rename test cycle</h3>
            <p className="te-modal-sub">Enter a new name for this test cycle</p>
            <input
              className="te-modal-input"
              type="text"
              value={renameCycleInput}
              onChange={e => setRenameCycleInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleRenameCycleSave(); if (e.key === 'Escape') closeRenameCycleModal(); }}
              autoFocus
            />
            <div className="te-modal-actions">
              <button className="te-modal-cancel" onClick={closeRenameCycleModal}>Cancel</button>
              <button
                className="te-modal-save"
                onClick={handleRenameCycleSave}
                disabled={!renameCycleInput.trim() || renameCycleInput.trim() === renameCycleModal.name || renameCycleSaving}
              >
                {renameCycleSaving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
      <CreateTestRunModal
        isOpen={showCreateRunModal}
        onClose={() => setShowCreateRunModal(false)}
        onCreated={(cycleId) => {
          setShowCreateRunModal(false);
          api.get(`${QUALITY_SERVICE_API_URL}/hub/test-execution/cycles`, { params: { project: selectedProject } })
            .then(res => {
              setCycles(res.data);
              setExpandedCycle(cycleId);
              api.get(`${QUALITY_SERVICE_API_URL}/hub/test-execution/runs`, { params: { testCycle: cycleId } })
                .then(runsRes => setRunsByCycle(prev => ({
                  ...prev,
                  [cycleId]: runsRes.data.map(r => ({ id: r.testRunId, name: r.name, promotedAs: r.promotedAs ?? [] })),
                })))
                .catch(() => {});
            })
            .catch(() => {});
        }}
        selectedProject={selectedProject}
      />
      <CreateTestRunModal
        isOpen={!!editRun}
        mode="edit"
        existingRun={editRun}
        onClose={() => setEditRun(null)}
        onUpdated={(testRunId) => {
          setEditRun(null);
          if (selectedRun === testRunId) {
            setRunLoading(true);
            api.get(`${QUALITY_SERVICE_API_URL}/hub/test-execution/run/${testRunId}`)
              .then(res => setRunData(res.data))
              .catch(() => {})
              .finally(() => setRunLoading(false));
          }
          if (expandedCycle) {
            api.get(`${QUALITY_SERVICE_API_URL}/hub/test-execution/runs`, { params: { testCycle: expandedCycle } })
              .then(res => setRunsByCycle(prev => ({
                ...prev,
                [expandedCycle]: res.data.map(r => ({ id: r.testRunId, name: r.name, promotedAs: r.promotedAs ?? [] })),
              })))
              .catch(() => {});
          }
        }}
        selectedProject={selectedProject}
      />
    </div>
  );
}

export default TestExecution;
